import { FC, useContext } from "react";
import { Link, useRouteMatch } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Box, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import __ from "src/utils/Translations";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesAbsencesConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import {
  useFetchAbsenceGroups,
  useChangeStatusAbsenceGroup,
} from "src/Pages/Company/Absences/api/useAbsenceGroups";
import useBasePath from "src/utils/hooks/useBasePath";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import { dynamicNotification } from "src/utils/Notifications";
import filterAbsenceGroupData from "src/utils/Company/Absences/filterAbsenceGroupData";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import Title from "src/Pages/Company/Absences/Title/Title";
import Tabs from "src/Pages/Company/Absences/Tabs/Tabs";
import PermissionsAlert from "src/Pages/Company/Absences/PermissionsAlert";
import AbsenceGroupsList from "src/Pages/Company/Absences/AbsenceGroups/AbsenceGroupsList";
import { IAbsenceType } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import { ErrorCodes, ICustomErrorDetails } from "./AbsenceGroups.models";

const AbsenceGroups: FC = () => {
  const match = useRouteMatch();
  const companyId = useCompanyId();
  const queryClient = useQueryClient();
  const basePath = useBasePath();
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );
  const logStatus = useLogStatus();
  const { setModal, resetModal } = useContext(GlobalModalContext);
  const {
    data: absenceGroupsData,
    isFetching,
    isError,
  } = useFetchAbsenceGroups(companyId);
  const absenceGroupMutation = useChangeStatusAbsenceGroup();

  const [activeGroups, inactiveGroups] =
    filterAbsenceGroupData(absenceGroupsData);

  const toggleActivation = (
    groupId: string,
    groupName: string,
    active: boolean
  ) => {
    if (!groupId) {
      logStatus({
        level: LogLevel.DEBUG,
        message: "No absence group ID provided to `toggleActivation` callback.",
        file: "AbsenceGroups.tsx",
      });
    }

    absenceGroupMutation.mutate(
      { groupId, groupName, active },
      {
        onSuccess: () => {
          dynamicNotification(__("Pomyślnie zapisano grupę nieobecności."));
          queryClient.invalidateQueries({
            queryKey: ["absence-groups", { companyId }],
          });
        },
        onError: (error) => {
          if (
            !error.response?.data ||
            typeof setModal !== "function" ||
            error.response?.data?.errors.every(
              (error) =>
                error.code !==
                ErrorCodes.ABSENCE_TYPE_ALREADY_ASSIGNED_TO_ABSENCE_GROUP
            )
          ) {
            dynamicNotification(
              __("Nie udało się zapisać danych."),
              "error",
              "top-right",
              7000
            );
            return;
          }

          const customError: ICustomErrorDetails = error.response?.data;
          const alreadyAssignedAbsenceTypesError = customError.errors.find(
            (error) =>
              error.code ===
              ErrorCodes.ABSENCE_TYPE_ALREADY_ASSIGNED_TO_ABSENCE_GROUP
          );

          if (alreadyAssignedAbsenceTypesError) {
            const assignedAbsenceTypes =
              alreadyAssignedAbsenceTypesError.details.assignedAbsenceTypes
                .map((type: IAbsenceType) => type.name)
                .join(", ");

            setModal({
              open: true,
              title: (
                <>
                  {__("Nie można dezaktywować grupy")}{" "}
                  <strong style={{ color: "#036BAE" }}>{groupName}</strong>.
                </>
              ),
              description: `${__(
                "Nie można dezaktywować grupy, ponieważ jest ona przypisana do rodzaju nieobecności"
              )} ${assignedAbsenceTypes}.`,
              actions: [
                {
                  label: __("Zamknij"),
                  variant: "contained",
                  callbackFn: () => {
                    resetModal();
                  },
                },
              ],
            });
          }
        },
      }
    );
  };

  const config = {
    id: "absence-groups",
    content: [
      {
        label: __("Aktywne"),
        component: (
          <AbsenceGroupsList
            isFetching={isFetching}
            data={activeGroups}
            toggleActivation={toggleActivation}
          />
        ),
      },
      {
        label: __("Nieaktywne"),
        component: (
          <AbsenceGroupsList
            isFetching={isFetching}
            data={inactiveGroups}
            toggleActivation={toggleActivation}
          />
        ),
      },
    ],
  };

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Grupy nieobecności i statusy obecności"),
          },
        ]}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Title
          title={__("Grupy nieobecności i statusy obecności")}
          subtitle={__(
            "Przestrzeń do tworzenia grup nieobecności i statusów obecności. Podczas edycji grupy możesz dodać nazwę, opis, ustawić kolejność wyświetlania oraz przypisać dedykowany kolor, aby wyróżnić grupę."
          )}
        />
        <Box flexShrink={0}>
          {canWriteAbsencesConfiguration && (
            <Button
              component={Link}
              variant="contained"
              to={`${match.url}/new`}
              startIcon={<AddIcon />}
              sx={{ marginBottom: "auto" }}
              data-testid="absence-groups__button-add"
            >
              {__("Nowa grupa")}
            </Button>
          )}
        </Box>
      </Box>
      {!canWriteAbsencesConfiguration && <PermissionsAlert />}
      {isError ? (
        <ErrorDataFetching />
      ) : (
        <Tabs
          config={config}
          label="Grupy nieobecności"
          wrapContent
          dataTestId="absence-groups-tabs"
        />
      )}
    </>
  );
};

export default AbsenceGroups;
